import { Copy, FolderOpen, MoreHorizontal, Pencil, Plus, RotateCcw, Search, Sparkles, Trash2 } from 'lucide-react';
import { useMemo, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  useCreateScene,
  useDeleteSceneForever,
  useDuplicateScene,
  useRenameScene,
  useRestoreScene,
  useScenes,
  useTrashScene,
} from '@/api/queries';
import { ApiError } from '@/api/client';
import type { SceneListItem } from '@/api/types';
import { Logo, UserMenu } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/form';
import { Dialog, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/components/ui/overlay';
import { emptyScene, exampleScene } from '@/editor/model';
import { ScenePreview } from '@/editor/ScenePreview';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';

function SceneCard({
  scene,
  trash,
  onRename,
  onDeleteForever,
}: {
  scene: SceneListItem;
  trash: boolean;
  onRename: () => void;
  onDeleteForever: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const duplicate = useDuplicateScene();
  const trashScene = useTrashScene();
  const restore = useRestoreScene();

  const moveToTrash = () =>
    trashScene.mutate(scene.id, {
      onSuccess: () =>
        toast(t('scenes.movedToTrash', { name: scene.name }), {
          action: { label: t('scenes.undo'), onClick: () => restore.mutate(scene.id) },
        }),
    });

  const body = (
    <>
      <div className="aspect-[3/2] overflow-hidden rounded-t-[7px] border-b border-line bg-surface-2">
        <ScenePreview state={scene.state} className="h-full w-full" />
      </div>
      <div className="min-w-0 px-3.5 pt-2.5 pb-3">
        <h3 className="truncate text-[1.02rem] font-bold">{scene.name}</h3>
        <p className="mt-0.5 truncate text-sm text-ink-2">
          {trash && scene.deletedAt
            ? t('scenes.deleted', { when: relativeTime(scene.deletedAt) })
            : t('scenes.updated', { when: relativeTime(scene.updatedAt) })}
        </p>
        <p className="mt-1 flex gap-2.5 text-[0.8rem] text-ink-3 tabular-nums">
          <span>{t('scenes.tokens', { count: scene.tokenCount })}</span>
          <span>{t('scenes.areas', { count: scene.areaCount })}</span>
          <span>{t('scenes.grid', { cols: scene.cols, rows: scene.rows })}</span>
        </p>
      </div>
    </>
  );

  return (
    <article className="group relative rounded-lg border border-line bg-surface shadow-panel transition-[border-color,transform] hover:-translate-y-0.5 hover:border-line-strong">
      {trash ? (
        <div className="opacity-80">{body}</div>
      ) : (
        <Link to={`/escenas/${scene.id}`} className="block rounded-lg focus-visible:outline-offset-4">
          {body}
        </Link>
      )}
      <div className="absolute top-2 right-2">
        <Menu>
          <MenuTrigger asChild>
            <Button size="icon-sm" variant="secondary" className="bg-surface/90 backdrop-blur" aria-label={scene.name}>
              <MoreHorizontal size={16} />
            </Button>
          </MenuTrigger>
          <MenuContent>
            {trash ? (
              <>
                <MenuItem
                  icon={<RotateCcw />}
                  onSelect={() =>
                    restore.mutate(scene.id, { onSuccess: () => toast(t('scenes.restored', { name: scene.name })) })
                  }
                >
                  {t('common.restore')}
                </MenuItem>
                <MenuSeparator />
                <MenuItem icon={<Trash2 />} danger onSelect={onDeleteForever}>
                  {t('scenes.deleteForever')}
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem icon={<FolderOpen />} onSelect={() => void navigate(`/escenas/${scene.id}`)}>
                  {t('common.open')}
                </MenuItem>
                <MenuItem icon={<Pencil />} onSelect={onRename}>
                  {t('common.rename')}
                </MenuItem>
                <MenuItem
                  icon={<Copy />}
                  onSelect={() =>
                    duplicate.mutate(scene.id, {
                      onSuccess: () => toast(t('scenes.duplicated')),
                      onError: (e) =>
                        toast.error(
                          e instanceof ApiError && e.code === 'quota_exceeded' ? t('scenes.quota') : t('common.error'),
                        ),
                    })
                  }
                >
                  {t('common.duplicate')}
                </MenuItem>
                <MenuSeparator />
                <MenuItem icon={<Trash2 />} danger onSelect={moveToTrash}>
                  {t('scenes.moveToTrash')}
                </MenuItem>
              </>
            )}
          </MenuContent>
        </Menu>
      </div>
    </article>
  );
}

export function ScenesPage() {
  const { t } = useTranslation();
  const errorText = (e: unknown) =>
    e instanceof ApiError && e.code === 'quota_exceeded' ? t('scenes.quota') : t('common.error');
  const navigate = useNavigate();
  const [trash, setTrash] = useState(false);
  const [query, setQuery] = useState('');
  const scenes = useScenes(trash);
  const create = useCreateScene();
  const rename = useRenameScene();
  const deleteForever = useDeleteSceneForever();
  const [renaming, setRenaming] = useState<SceneListItem | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState<SceneListItem | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (scenes.data ?? []).filter((s) => !q || s.name.toLowerCase().includes(q));
  }, [scenes.data, query]);

  const createScene = (example: boolean) =>
    create.mutate(
      example
        ? {
            name: t('scenes.exampleName'),
            state: exampleScene({
              spikeGrowth: t('presets.spikeGrowth'),
              hungerOfHadar: t('presets.hungerOfHadar'),
              burningHands: t('presets.burningHands'),
            }),
          }
        : { name: t('scenes.untitled'), state: emptyScene() },
      { onSuccess: (scene) => void navigate(`/escenas/${scene.id}`), onError: (e) => toast.error(errorText(e)) },
    );

  const submitRename = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!renaming || !renameValue.trim()) return;
    rename.mutate({ id: renaming.id, name: renameValue.trim() }, { onSuccess: () => setRenaming(null) });
  };

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-20 border-b border-line bg-bg/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <Logo />
          <UserMenu />
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 pt-7 pb-16 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-[2rem] leading-none">{trash ? t('scenes.trash') : t('scenes.title')}</h1>
            <div className="mt-3 flex gap-1">
              {[false, true].map((isTrash) => (
                <button
                  key={String(isTrash)}
                  type="button"
                  onClick={() => setTrash(isTrash)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-[0.93rem] font-medium',
                    trash === isTrash ? 'bg-surface-3 text-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
                  )}
                >
                  {isTrash ? t('scenes.trash') : t('scenes.title')}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-3" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('scenes.search')}
                aria-label={t('scenes.search')}
                className="w-56 pl-8"
              />
            </div>
            {!trash && (
              <>
                <Button onClick={() => createScene(true)} disabled={create.isPending}>
                  <Sparkles size={16} />
                  {t('scenes.example')}
                </Button>
                <Button variant="primary" onClick={() => createScene(false)} disabled={create.isPending}>
                  <Plus size={17} />
                  {t('scenes.new')}
                </Button>
              </>
            )}
          </div>
        </div>

        {scenes.isPending && <p className="mt-10 text-ink-2">{t('common.loading')}</p>}
        {scenes.isError && (
          <p className="mt-10 text-danger">
            {t('common.error')}{' '}
            <button type="button" className="underline" onClick={() => void scenes.refetch()}>
              {t('common.retry')}
            </button>
          </p>
        )}

        {scenes.data && scenes.data.length === 0 && (
          <div className="mt-12 flex flex-col items-center rounded-xl border border-dashed border-line-strong px-6 py-14 text-center">
            <svg viewBox="0 0 120 80" className="mb-5 h-20 w-32" aria-hidden>
              <rect x="2" y="2" width="116" height="76" rx="6" fill="var(--c-mat)" stroke="var(--c-line-strong)" />
              <path
                d="M22 2v76M42 2v76M62 2v76M82 2v76M102 2v76M2 22h116M2 42h116M2 62h116"
                stroke="var(--c-grid)"
                strokeOpacity=".3"
              />
              <circle
                cx="72"
                cy="32"
                r="18"
                fill="var(--c-accent)"
                fillOpacity=".14"
                stroke="var(--c-accent)"
                strokeDasharray="4 3"
              />
              <circle cx="52" cy="52" r="8" fill="var(--c-accent)" />
            </svg>
            <p className="text-lg font-medium">{trash ? t('scenes.emptyTrash') : t('scenes.empty')}</p>
            {!trash && (
              <>
                <p className="mt-1 max-w-md text-ink-2">{t('scenes.emptyHint')}</p>
                <div className="mt-5 flex gap-2">
                  <Button onClick={() => createScene(true)} disabled={create.isPending}>
                    <Sparkles size={16} />
                    {t('scenes.example')}
                  </Button>
                  <Button variant="primary" onClick={() => createScene(false)} disabled={create.isPending}>
                    <Plus size={17} />
                    {t('scenes.new')}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {scenes.data && scenes.data.length > 0 && filtered.length === 0 && (
          <p className="mt-10 text-ink-2">{t('scenes.noResults', { query })}</p>
        )}

        <div className="mt-7 grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-5">
          {filtered.map((scene) => (
            <SceneCard
              key={scene.id}
              scene={scene}
              trash={trash}
              onRename={() => {
                setRenaming(scene);
                setRenameValue(scene.name);
              }}
              onDeleteForever={() => setDeleting(scene)}
            />
          ))}
        </div>
      </main>

      <Dialog
        open={renaming !== null}
        onOpenChange={(open) => !open && setRenaming(null)}
        title={t('scenes.renameTitle')}
      >
        <form onSubmit={submitRename} className="flex flex-col gap-4">
          <Input
            autoFocus
            value={renameValue}
            maxLength={80}
            onChange={(e) => setRenameValue(e.target.value)}
            aria-label={t('common.name')}
          />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setRenaming(null)}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={!renameValue.trim() || rename.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('scenes.deleteForeverTitle', { name: deleting?.name ?? '' })}
        description={t('scenes.deleteForeverBody')}
        footer={
          <>
            <Button onClick={() => setDeleting(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              disabled={deleteForever.isPending}
              onClick={() => deleting && deleteForever.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
            >
              <Trash2 size={16} />
              {t('scenes.deleteForever')}
            </Button>
          </>
        }
      />
    </div>
  );
}
